<template>
    <div class="event-management">
        <DevelopmentWarning />

        <div class="header-section">
            <div class="header-left">
                <h2>Event Management</h2>
                <div class="filter-section">
                    <select v-model="selectedEventType" class="filter-dropdown">
                        <option value="">All Event Types</option>
                        <option v-for="type in eventTypes" :key="type" :value="type">
                            {{ type }}
                        </option>
                    </select>
                    <select v-model="selectedTimeFilter" class="filter-dropdown">
                        <option value="all">All Events</option>
                        <option value="upcoming">Upcoming Events</option>
                        <option value="past">Past Events</option>
                    </select>
                </div>
            </div>
            <button @click="showAddEventModal = true" class="add-btn">Add New Event</button>
        </div>

        <!-- Events List -->
        <div class="events-table">
            <table>
                <thead>
                    <tr>
                        <th @click="sortBy('event_date')" class="sortable text-left">
                            <span class="desktop-text">Date & Time</span>
                            <span class="mobile-text">Date</span>
                            <span class="sort-icon" v-if="sortField === 'event_date'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th @click="sortBy('event_type')" class="sortable text-left">
                            Type
                            <span class="sort-icon" v-if="sortField === 'event_type'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th @click="sortBy('venue_name')" class="sortable text-left">
                            Venue
                            <span class="sort-icon" v-if="sortField === 'venue_name'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th @click="sortBy('attendance')" class="sortable text-left">
                            <span class="desktop-text">Attendance</span>
                            <span class="mobile-text">Attend</span>
                            <span class="sort-icon" v-if="sortField === 'attendance'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="event in filteredEvents" 
                        :key="event.id"
                        :class="{ 'past-event': getEventStatus(event.event_date, event.end_time) === 'past' }"
                    >
                        <td class="text-left">
                            <div class="date-display">
                                <div class="weekday">{{ new Date(event.event_date).toLocaleDateString('en-US', { weekday: 'short' }) }}</div>
                                <div class="date">{{ new Date(event.event_date).getDate() }} {{ new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' }) }} {{ new Date(event.event_date).getFullYear().toString().substr(2) }}</div>
                                <div class="time">{{ formatTime(event.event_date) }}</div>
                            </div>
                        </td>
                        <td class="text-left">
                            <span class="event-type" :class="getEventTypeClass(event.event_type)">
                                {{ event.event_type }}
                            </span>
                            <div v-if="event.format_id" class="format-name">{{ getFormatName(event.format_id) }}</div>
                        </td>
                        <td class="text-left">{{ event.venue_name }}</td>
                        <td class="text-left">
                            <span class="attendance-count">
                                {{ event.attendance?.attending_count || 0 }} / {{ event.attendance?.total_members || 0 }}
                            </span>
                        </td>
                        <td class="actions-cell">
                            <div class="dropdown" :class="{ active: activeDropdown === event.id }">
                                <button class="btn-icon" @click.stop="toggleDropdown(event)">
                                    <i class="material-icons">more_vert</i>
                                </button>
                                <div class="dropdown-menu" v-if="activeDropdown === event.id">
                                    <button class="dropdown-item" @click="viewEventDetails(event)">
                                        <i class="material-icons">visibility</i>
                                        <span>View</span>
                                    </button>
                                    <button class="dropdown-item" @click="editEvent(event)">
                                        <i class="material-icons">edit</i>
                                        <span>Edit</span>
                                    </button>
                                    <button class="dropdown-item" @click="confirmDelete(event)">
                                        <i class="material-icons">delete</i>
                                        <span>Delete</span>
                                    </button>
                                    <button class="dropdown-item" @click="generateAttendanceSheet(event)">
                                        <i class="material-icons">print</i>
                                        <span>Print Member Sheet</span>
                                    </button>
                                    <button 
                                        class="dropdown-item" 
                                        v-if="event.event_type === 'Regular Meeting'"
                                        @click="showHeatMap(event)"
                                    >
                                        <i class="material-icons">bar_chart</i>
                                        <span>Check-in Heatmap</span>
                                    </button>
                                    <button 
                                        class="dropdown-item" 
                                        v-if="event.event_type === 'Regular Meeting'"
                                        @click="printAgenda(event)"
                                    >
                                        <i class="material-icons">print</i>
                                        <span>Print Agenda</span>
                                    </button>
                                    <button 
                                        class="dropdown-item" 
                                        @click="showNotificationModal(event)"
                                    >
                                        <i class="material-icons">mail</i>
                                        <span>Send Notifications</span>
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Add/Edit Event Modal -->
        <EventModal
            v-if="showAddEventModal"
            :is-editing="!!editingEvent"
            :editing-event="editingEvent"
            :venues="venues"
            :members="members"
            :formats="formats"
            @save="saveEvent"
            @close="closeEventModal"
            @update:members="members = $event"
        />

        <!-- Event Details Modal -->
        <EventDetailsModal
            v-if="showDetailsModal"
            :event="selectedEvent"
            :current-user="currentUser"
            :formats="formats"
            :is-editing="false"
            @close="closeDetailsModal"
            @session-updated="loadEvents"
        />

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal">
            <div class="modal-content delete-confirmation">
                <h3>Confirm Delete</h3>
                <p>Are you sure you want to delete this event?</p>
                <div class="event-summary">
                    <p><strong>Date:</strong> {{ formatDate(eventToDelete?.event_date) }}</p>
                    <p><strong>Type:</strong> {{ eventToDelete?.event_type }}</p>
                    <p><strong>Venue:</strong> {{ eventToDelete?.venue_name }}</p>
                </div>
                <div class="modal-actions">
                    <button @click="deleteEvent" class="delete-btn">Delete</button>
                    <button @click="cancelDelete" class="cancel-btn">Cancel</button>
                </div>
            </div>
        </div>

        <!-- Heat Map Modal -->
        <HeatMapModal
            v-if="showingHeatMap"
            :meeting="selectedEvent"
            @close="showingHeatMap = false"
        />

        <!-- Add the notification modal -->
        <EventNotificationModal
            v-if="showingNotificationModal"
            :show="showingNotificationModal"
            :event="selectedEvent"
            :members="members"
            @close="closeNotificationModal"
            @notifications-sent="handleNotificationsSent"
        />
    </div>
</template>

<script>
import { ref, onMounted, computed, watch, onBeforeUnmount } from 'vue';
import { eventService } from '../services/eventService';
import { jsPDF } from 'jspdf';
import EventDetailsModal from './EventDetailsModal.vue';
import HeatMapModal from './HeatMapModal.vue';
import ActionLogo from '@/assets/action-logo.svg';
import { generateAgendaPDF } from '../services/agendaPdfService';
import EventNotificationModal from './EventNotificationModal.vue';
import { userService } from '@/services/api';
import DevelopmentWarning from './common/DevelopmentWarning.vue';
import EventModal from './EventModal.vue';

const roundToNearest15Minutes = (date) => {
    const minutes = date.getMinutes();
    const roundTo = 15;
    const roundedMinutes = Math.floor(minutes / roundTo) * roundTo;
    date.setMinutes(roundedMinutes);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
};

const convertSvgToPng = (svgElement, width, height) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        const img = new Image();
        img.onload = () => {
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png'));
        };
        img.src = svgElement;
    });
};

export default {
    name: 'EventManagement',
    components: {
        EventDetailsModal,
        HeatMapModal,
        EventNotificationModal,
        DevelopmentWarning,
        EventModal
    },
    setup() {
        const currentUser = ref(null);
        const events = ref([]);
        const venues = ref([]);
        const members = ref([]);
        const showAddEventModal = ref(false);
        const editingEvent = ref(null);
        const error = ref('');
        const selectedEventType = ref('');
        const sortField = ref('event_date');
        const sortDirection = ref('asc');
        const showDetailsModal = ref(false);
        const showDeleteModal = ref(false);
        const selectedEvent = ref(null);
        const eventToDelete = ref(null);
        const selectedTimeFilter = ref('upcoming');
        const totalMembers = ref(0);
        const showingHeatMap = ref(false);
        const activeDropdown = ref(null);
        const showActionsMenu = ref({});
        const showingNotificationModal = ref(false);
        const formats = ref([]);

        const eventTypes = [
            'Regular Meeting',
            'Network Event',
            'Social Event'
        ];

        const generateTimeSlots = () => {
            const slots = [];
            for (let hour = 0; hour < 24; hour++) {
                for (let minute = 0; minute < 60; minute += 15) {
                    const hourStr = hour.toString().padStart(2, '0');
                    const minStr = minute.toString().padStart(2, '0');
                    slots.push(`${hourStr}:${minStr}`);
                }
            }
            return slots;
        };

        const timeSlots = generateTimeSlots();

        const defaultEventForm = {
            event_type: 'Regular Meeting',
            start_date: new Date().toISOString().split('T')[0],
            start_time: '08:00',
            end_date: new Date().toISOString().split('T')[0],
            end_time: '10:00',
            venue_id: '',
            intro_topic: '',
            sessions: []
        };

        const eventForm = ref({ ...defaultEventForm });

        // Watch meeting format changes to initialize sessions array
        watch(() => eventForm.value.format_id, (newFormatId) => {
            if (!newFormatId) {
                eventForm.value.sessions = [];
                return;
            }
            
            const format = formats.value.find(f => f.id === newFormatId);
            if (!format) return;

            // Initialize sessions based on format
            eventForm.value.sessions = format.sessions.map(session => ({
                id: session.id || null,
                speaker_id: '',
                topic: '',
                description: '',
                session_type: session.session_type || session.type || 'hot_seat',
                speaker_order: session.speaker_order || 1,
                guest_speaker_name: session.guest_speaker_name || null
            }));
        });

        watch(() => eventForm.value.start_date, (newDate) => {
            eventForm.value.end_date = newDate;
        });

        // Reset session fields when event type changes
        watch(() => eventForm.value.event_type, (newType) => {
            if (newType !== 'Regular Meeting') {
                eventForm.value.intro_topic = '';
                eventForm.value.format_id = '';
                eventForm.value.sessions = [];
            }
        });

        const getEventStatus = (eventDate, endTime) => {
            const now = new Date();
            const eventTime = new Date(eventDate);
            const eventEndTime = new Date(endTime);
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const eventDay = new Date(eventTime.getFullYear(), eventTime.getMonth(), eventTime.getDate());

            // Check if event has ended
            if (eventEndTime < now) {
                return 'past';
            } else {
                // If event is today or in the future, mark as upcoming
                return eventDay >= today ? 'upcoming' : 'past';
            }
        };

        const filteredEvents = computed(() => {
            let filtered = events.value || [];
            
            // Filter by event type
            if (selectedEventType.value) {
                filtered = filtered.filter(event => event.event_type === selectedEventType.value);
            }
            
            // Filter by time
            if (selectedTimeFilter.value !== 'all') {
                filtered = filtered.filter(event => {
                    const status = getEventStatus(event.event_date, event.end_time);
                    return status === selectedTimeFilter.value;
                });
            }
            
            // Sort the filtered events
            if (sortField.value) {
                filtered.sort((a, b) => {
                    let aValue, bValue;
                    
                    switch (sortField.value) {
                        case 'event_date':
                            aValue = new Date(a.event_date).getTime();
                            bValue = new Date(b.event_date).getTime();
                            break;
                        case 'event_type':
                            aValue = a.event_type;
                            bValue = b.event_type;
                            break;
                        case 'venue_name':
                            aValue = a.venue_name;
                            bValue = b.venue_name;
                            break;
                        case 'attendance':
                            aValue = a.attendance?.attending_count || 0;
                            bValue = b.attendance?.attending_count || 0;
                            break;
                        default:
                            return 0;
                    }
                    
                    // Handle string comparison
                    if (typeof aValue === 'string') {
                        aValue = aValue.toLowerCase();
                        bValue = bValue.toLowerCase();
                    }
                    
                    // Apply sort direction
                    const modifier = sortDirection.value === 'asc' ? 1 : -1;
                    if (aValue < bValue) return -1 * modifier;
                    if (aValue > bValue) return 1 * modifier;
                    return 0;
                });
            }
            
            return filtered;
        });

        const getEventTypeClass = (type) => {
            switch (type) {
                case 'Regular Meeting':
                    return 'event-type-regular';
                case 'Network Event':
                    return 'event-type-network';
                case 'Social Event':
                    return 'event-type-social';
                default:
                    return '';
            }
        };

        const loadEvents = async () => {
            try {
                const response = await eventService.getAllEvents();

                const eventsWithAttendance = await Promise.all(response.map(async event => {
                    try {

                        const attendance = await eventService.getEventAttendance(event.id);
                        return {
                            ...event,
                            attendance: {
                                attending_count: attendance.attending_count,
                                total_members: attendance.total_members
                            }
                        };
                    } catch (err) {
                        console.error(`Error getting attendance for event ${event.id}:`, err);
                        return {
                            ...event,
                            attendance: { attending_count: 0, total_members: 0 }
                        };
                    }
                }));

                events.value = eventsWithAttendance;
            } catch (error) {
                console.error('Error loading events:', error);
            }
        };

        const combineDateTime = (date, time) => {
            const [hours, minutes] = time.split(':');
            const dateObj = new Date(date);
            dateObj.setHours(parseInt(hours, 10));
            dateObj.setMinutes(parseInt(minutes, 10));
            return dateObj;
        };

        const editEvent = async (event) => {
            try {
                const eventDetails = await eventService.getEventDetails(event.id);

                // Get format_id from event details
                const format_id = eventDetails.format_id;

                // Prepare event data for editing
                const eventToEdit = {
                    id: eventDetails.id,
                    event_type: eventDetails.event_type || 'Regular Meeting',
                    format_id: format_id,
                    event_date: eventDetails.event_date,
                    end_time: eventDetails.end_time,
                    venue_id: eventDetails.venue_id,
                    intro_topic: eventDetails.intro_topic || '',
                    sessions: eventDetails.sessions || []
                };


                editingEvent.value = eventToEdit;
                showAddEventModal.value = true;
                closeDropdown();
            } catch (error) {
                console.error('[editEvent] Error:', error);
            }
        };

        const closeEventModal = () => {
            // Hide modal first
            showAddEventModal.value = false;
            // Then clear data
            editingEvent.value = null;
        };

        const formatDate = (date) => {
            // Convert to Brisbane time
            const brisbaneDate = new Date(date).toLocaleString('en-AU', {
                timeZone: 'Australia/Brisbane',
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return brisbaneDate;
        };

        const confirmDelete = (event) => {
            eventToDelete.value = event;
            showDeleteModal.value = true;
        };

        const deleteEvent = async () => {
            try {
                await eventService.deleteEvent(eventToDelete.value.id);
                await loadEvents();
                showDeleteModal.value = false;
                eventToDelete.value = null;
            } catch (err) {
                error.value = 'Failed to delete event';
            }
        };

        const cancelDelete = () => {
            showDeleteModal.value = false;
            eventToDelete.value = null;
        };

        const closeDetailsModal = () => {
            showDetailsModal.value = false;
            selectedEvent.value = null;
        };

        const getEventSpeakerName = (speakerId) => {
            const speaker = members.value.find(m => m.id === speakerId);
            return speaker ? `${speaker.first_name} ${speaker.last_name}` : 'Not assigned';
        };

        const sortBy = (field) => {
            if (sortField.value === field) {
                sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
            } else {
                sortField.value = field;
                sortDirection.value = 'asc';
            }
        };

        const viewEventDetails = async (event) => {
            try {
                // Load formats and event details first
                const [, eventDetails] = await Promise.all([
                    loadFormats(),
                    eventService.getEventDetails(event.id)
                ]);
                
                // Set data and show modal
                selectedEvent.value = eventDetails;
                showDetailsModal.value = true;
                activeDropdown.value = null;
            } catch (error) {
                console.error('Error fetching event details:', error);
            }
        };

        const formatDateForInput = (dateString) => {
            const date = new Date(dateString);
            return roundToNearest15Minutes(date).toISOString().slice(0, 16);
        };

        const validateEventTimes = (eventData) => {
            const startTime = new Date(eventData.event_date);
            const endTime = new Date(eventData.end_time);
            
            if (endTime <= startTime) {
                error.value = 'End time must be after start time';
                return false;
            }
            return true;
        };

        const handleTimeChange = (field) => {
            const date = new Date(eventForm.value[field]);
            const roundedDate = roundToNearest15Minutes(date);
            eventForm.value[field] = roundedDate.toISOString().slice(0, 16);
            validateEventTimes();
        };

        const formatShortDate = (date) => {
            if (!date) return '';
            const brisbaneDate = new Date(date).toLocaleString('en-AU', {
                timeZone: 'Australia/Brisbane',
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
            return brisbaneDate;
        };

        const formatTime = (date) => {
            if (!date) return '';
            const brisbaneTime = new Date(date).toLocaleString('en-AU', {
                timeZone: 'Australia/Brisbane',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return brisbaneTime;
        };

        const getAttendingMembers = (event) => {
            if (!event.attendees) return 'None';
            const attending = event.attendees
                .filter(a => a.attending)
                .map(a => a.name)
                .join(', ');
            return attending || 'None';
        };

        const getNotAttendingMembers = (event) => {
            if (!event.attendees) return 'None';
            const notAttending = event.attendees
                .filter(a => !a.attending)
                .map(a => a.name)
                .join(', ');
            return notAttending || 'None';
        };

        const getNoResponseMembers = (event) => {
            if (!event.no_response) return 'None';
            return event.no_response.join(', ') || 'None';
        };

        const showHeatMap = (event) => {
            selectedEvent.value = event;
            showingHeatMap.value = true;
            closeDropdown();
        };

        const toggleDropdown = (event) => {
            activeDropdown.value = activeDropdown.value === event.id ? null : event.id;
        };

        const handleClickOutside = (event) => {
            const dropdowns = document.querySelectorAll('.actions-dropdown');
            let clickedInside = false;
            
            dropdowns.forEach(dropdown => {
                if (dropdown.contains(event.target)) {
                    clickedInside = true;
                }
            });
            
            if (!clickedInside) {
                Object.keys(showActionsMenu.value).forEach(key => {
                    showActionsMenu.value[key] = false;
                });
            }
        };

        const generateAttendanceSheet = async (event) => {
            try {
                const members = await userService.getAllMembersWithPreferences();
                // Convert SVG logo to PNG
                const logoDataUrl = await convertSvgToPng(ActionLogo, 120, 48);
                
                // Create PDF document
                const doc = new jsPDF();
                
                // Add Action Network logo
                doc.addImage(logoDataUrl, 'PNG', 15, 10, 30, 12);
                
                // Add event details with smaller font
                doc.setFontSize(14);
                doc.text(`Event: ${event.event_type}`, 15, 30);
                doc.text(`Date: ${formatShortDate(event.event_date)}`, 120, 30);
                doc.text(`Venue: ${event.venue_name}`, 120, 37);
                
                // Add column headers
                doc.setFontSize(10);
                doc.text('Name', 20, 48);
                doc.text('Coffee', 60, 48);
                doc.text('Dietary', 130, 48);

                // Add separator line
                doc.line(15, 51, 195, 51);

                // Add attendee list with smaller font and spacing
                doc.setFontSize(9);
                let yPos = 58;

                members.forEach((member) => {
                    doc.rect(15, yPos - 3, 3, 3);

                    const coffee1 = member.coffee_order_1 || '_____________________';
                    const coffee1Lines = doc.splitTextToSize(coffee1, 65);
                    const dietaryLines = doc.splitTextToSize(member.dietary_requirements || '', 65);

                    doc.text(`${member.first_name} ${member.last_name}`, 20, yPos);
                    doc.text(coffee1Lines, 60, yPos);
                    doc.text(dietaryLines, 130, yPos);

                    const maxLines = Math.max(coffee1Lines.length, dietaryLines.length, 1);
                    yPos += (maxLines * 4) + 4;
                    
                    // Add new page if needed
                    if (yPos > 280) {
                        doc.addPage();
                        yPos = 20;
                    }
                });
                
                // Add footer for Regular Meeting events only
                if (event.event_type === 'Regular Meeting') {
                    doc.setFontSize(8);
                    doc.setFont(undefined, 'italic');
                    
                    // Calculate footer position - 20 points from bottom of page
                    const pageHeight = doc.internal.pageSize.height;
                    const footerY = pageHeight - 20;
                    
                    doc.text(
                        'Any extras for breakfast must be paid for by the member. ' +
                        'Visitors to pay for their own breakfast. ' +
                        'Email receipt to jordan@ChekRite.com.',
                        15, footerY
                    );
                }
                
                // Save the PDF
                doc.save(`attendance-sheet-${formatShortDate(event.event_date)}.pdf`);
                closeDropdown();
            } catch (error) {
                console.error('Error generating attendance sheet:', error);
            }
        };

        const loadVenues = async () => {
            try {
                const response = await eventService.getAllVenues();
                venues.value = response;
            } catch (err) {
                console.error('Error loading venues:', err);
            }
        };

        const loadMembers = async () => {
            try {
                const response = await eventService.getAllMembers();
                members.value = response;
            } catch (err) {
                console.error('Error loading members:', err);
            }
        };

        const loadFormats = async () => {
            try {
                formats.value = await eventService.getFormats();
            } catch (error) {
                console.error('Error loading formats:', error);
            }
        };

        const printAgenda = async (event) => {
            try {
                const eventDetails = await eventService.getEventDetails(event.id);
                await generateAgendaPDF(eventDetails, members.value);
                closeDropdown();
            } catch (error) {
                console.error('Error generating agenda PDF:', error);
            }
        };

        const closeDropdown = () => {
            activeDropdown.value = null;
        };

        const showNotificationModal = async (event) => {
            try {
                // Load complete event details including sessions
                const eventDetails = await eventService.getEventDetails(event.id);
                selectedEvent.value = eventDetails;

                // Load active members if needed
                if (!members.value.length) {
                    const response = await userService.getActiveMembers();
                    members.value = response;
                }
                
                showingNotificationModal.value = true;
                closeDropdown();
            } catch (error) {
                console.error('Error preparing notification modal:', error);
            }
        };

        const closeNotificationModal = () => {
            showingNotificationModal.value = false;
            selectedEvent.value = null;
        };

        const handleNotificationsSent = () => {
            // Show success message or handle any post-send actions
            // You might want to use your notification system here
        };

        const isFormValid = computed(() => {
            if (!eventForm.value.event_type) return false;
            if (!eventForm.value.venue_id) return false;
            if (!eventForm.value.start_date || !eventForm.value.start_time) return false;
            if (!eventForm.value.end_date || !eventForm.value.end_time) return false;
            
            if (eventForm.value.event_type === 'Regular Meeting') {
                if (!eventForm.value.format_id) return false;
                
                // Validate sessions based on format
                const sessions = eventForm.value.sessions || [];
                const format = formats.value.find(f => f.id === eventForm.value.format_id);
                if (!format) return false;
                
                // Validate that we have at least one speaker session with a speaker
                return sessions.some(session => 
                    (session.session_type === 'member_speaker' || session.session_type === 'external_speaker') && 
                    session.speaker_id
                );
            }
                        
            return true;
        });

        const fetchEvents = async () => {
            try {
                const response = await eventService.getAllEvents();
                events.value = response;
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };

        const saveEvent = async (event) => {
            try {
                
                if (event.isEditing) {
                    // Update existing event
                    const updateData = {
                        ...event.data,
                        event_date: event.data.event_date,  // Ensure this is passed through
                        end_time: event.data.end_time
                    };
                    
                    await eventService.updateEvent(event.editingEventId, updateData);
                } else {
                    // Create new event
                    await eventService.createEvent(event.data);
                }
                
                // Refresh events list
                await loadEvents();
            } catch (error) {
                console.error('Error saving event:', error);
            }
        };

        // Add this computed property
        const getFormatName = (formatId) => {
            if (!formatId || !formats.value) return '';
            const format = formats.value.find(f => f.id === formatId);
            return format ? format.name : '';
        };

        onMounted(async () => {
            document.addEventListener('click', handleClickOutside);
            await Promise.all([
                loadEvents(),
                loadVenues(),
                loadMembers(),
                loadFormats()
            ]);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside);
            editingEvent.value = null;
            showAddEventModal.value = false;
        });

        return {
            events,
            venues,
            members,
            showAddEventModal,
            eventForm,
            editingEvent,
            formatDate,
            editEvent,
            closeEventModal,
            eventTypes,
            selectedEventType,
            filteredEvents,
            getEventTypeClass,
            confirmDelete,
            sortField,
            sortDirection,
            showDetailsModal,
            showDeleteModal,
            selectedEvent,
            eventToDelete,
            viewEventDetails,
            closeDetailsModal,
            deleteEvent,
            cancelDelete,
            getEventSpeakerName,
            formatDateForInput,
            validateEventTimes,
            handleTimeChange,
            timeSlots,
            selectedTimeFilter,
            sortBy,
            getEventStatus,
            totalMembers,
            formatShortDate,
            formatTime,
            getAttendingMembers,
            getNotAttendingMembers,
            getNoResponseMembers,
            currentUser,
            showHeatMap,
            showingHeatMap,
            activeDropdown,
            toggleDropdown,
            generateAttendanceSheet,
            printAgenda,
            showActionsMenu,
            showingNotificationModal,
            showNotificationModal,
            closeNotificationModal,
            handleNotificationsSent,
            combineDateTime,
            isFormValid,
            saveEvent,
            fetchEvents,
            formats,
            getFormatName,
            loadEvents
        };
    }
};

</script>

<style scoped>
.event-management {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.add-btn {
    background-color: #4CAF50;
    color: white;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.events-table {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    overflow: visible;
    position: relative;
    z-index: 1;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th {
    text-align: left;
    padding: 1rem;
    background: #f8f9fa;
    font-weight: 500;
    color: #2c3e50;
    border-bottom: 2px solid #eee;
}

td {
    padding: 1rem;
    border-bottom: 1px solid #eee;
    vertical-align: middle;
}

tr:last-child td {
    border-bottom: none;
}

.actions {
    display: flex;
    gap: 0.5rem;
}

.edit-btn, .delete-btn {
    padding: 0.25rem 0.75rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
}

.edit-btn {
    background-color: #3498db;
    color: white;
}

.delete-btn {
    background-color: #dc3545;
    color: white;
}

.modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;
    overflow-y: auto;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 800px;
    max-height: calc(100vh - 4rem);
    overflow-y: auto;
}

.form-section {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 4px;
    margin-bottom: 1.5rem;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.form-group input,
.form-group select,
.form-group textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.session-details {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #ddd;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
}

.save-btn {
    background-color: #4CAF50;
    color: white;
    padding: 0.5rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.cancel-btn {
    background-color: #dc3545;
    color: white;
    padding: 0.5rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.form-group select {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
}

.event-type {
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    font-weight: 500;
}

.event-type-regular {
    background-color: #e3f2fd;
    color: #1976d2;
}

.event-type-network {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.event-type-social {
    background-color: #f3e5f5;
    color: #7b1fa2;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 2rem;
}

.filter-section {
    display: flex;
    gap: 1rem;
}

.filter-dropdown {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
    min-width: 150px;
    font-size: 0.9rem;
}

.sortable {
    cursor: pointer;
    user-select: none;
}

.sortable:hover {
    background-color: #f0f4f8;
}

.sort-icon {
    margin-left: 0.5rem;
    font-size: 0.8rem;
}

.view-btn {
    background-color: #6c757d;
    color: white;
    padding: 0.25rem 0.75rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
}

.event-details-grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin: 1rem 0;
}

.sessions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
}

.detail-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
}

.detail-section h4 {
    margin: 0 0 1rem 0;
    color: #2c3e50;
    font-size: 1.1rem;
    padding-left: 120px;
}

.details-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-width: 800px;
    margin: 0 auto;
}

.detail-row {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 1rem;
    align-items: baseline;
}

.detail-label {
    color: #666;
    font-weight: 500;
    font-size: 0.9rem;
    text-align: right;
}

.detail-value {
    color: #2c3e50;
    text-align: left;
    padding-left: 0;
    line-height: 1.5;
}

.detail-value:empty::after {
    content: 'None';
    color: #666;
    font-style: italic;
}

.detail-value.description {
    grid-column: 2;
    margin-top: 0.5rem;
    padding: 0.75rem;
    background: #fff;
    border-radius: 4px;
    font-style: italic;
}

.modal-content {
    max-width: 900px;
}

.delete-confirmation {
    max-width: 400px;
}

.event-summary {
    margin: 1rem 0;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 4px;
}

.error-message {
    background-color: #ffebee;
    color: #c62828;
    padding: 0.75rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    font-weight: 500;
}

.form-group input.invalid {
    border-color: #dc3545;
}

.date-time-group {
    margin-bottom: 1rem;
}

.date-time-inputs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.date-time-inputs input,
.date-time-inputs select {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
}

.past-event {
    background-color: #f8f9fa;
    color: #6c757d;
}

.past-event .event-type {
    opacity: 0.7;
}

.event-status {
    font-size: 0.75rem;
    padding: 0.2rem 0.5rem;
    border-radius: 1rem;
}

.event-status.past {
    background-color: #f8d7da;
    color: #842029;
}

.event-status.today {
    background-color: #fff3cd;
    color: #664d03;
}

.event-status.upcoming {
    background-color: #d1e7dd;
    color: #0f5132;
}

.text-left {
    text-align: left;
}

.date-display {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.time-display {
    color: #666;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.attendance-count {
    font-family: monospace;
    font-size: 0.95rem;
    padding: 0.2rem 0.5rem;
    background: #f0f4f8;
    border-radius: 4px;
}

.events-table th,
.events-table td {
    padding: 0.75rem 1rem;
    vertical-align: top;
}

.datetime-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
}

.datetime-column {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.datetime-inputs {
    display: grid;
    grid-template-columns: 1fr 120px;
    gap: 0.5rem;
}

.datetime-inputs input,
.datetime-inputs select {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.heat-map-btn {
    padding: 0.25rem 0.5rem;
    margin-left: 0.5rem;
    font-size: 1.2rem;
    line-height: 1;
}

.actions-cell {
    position: relative;
}

.dropdown {
    position: relative;
}

.dropdown-menu {
    position: absolute;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    min-width: 160px;
    top: 100%;
    right: 0;
    margin-top: 2px;
    z-index: 999;
}

.dropdown.active .btn-icon {
    visibility: visible;
}

.dropdown-item {
    width: 100%;
    border: none;
    background: none;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: background-color 0.2s;
    text-align: left;
    white-space: nowrap;
}

.dropdown-item:hover {
    background-color: #f5f5f5;
}

.btn-icon {
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    position: relative;
    z-index: 1;
}

.material-icons {
    font-size: 18px;
    flex-shrink: 0;
}

.dropdown-item:active {
    background-color: #e9ecef;
}

.dropdown-item i {
    margin-right: 8px;
}

.action-menu {
    position: absolute;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 100;
}

.action-button {
    /* existing styles */
}

.meeting-format {
    font-size: 0.9rem;
    color: #666;
    margin-top: 0.25rem;
}

.mobile-text {
    display: none;
}

.desktop-text {
    display: inline;
}

@media (max-width: 768px) {
    .mobile-text {
        display: inline;
    }

    .desktop-text {
        display: none;
    }

    .header-section {
        padding-top: 1rem;
        flex-direction: column;
        gap: 1rem;
    }

    .header-left {
        width: 100%;
    }

    .add-btn {
        width: 100%;
        margin-top: 0.5rem;
    }

    .filter-section {
        flex-direction: column;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    .filter-dropdown {
        width: 100%;
    }

    /* Hide venue and attendance columns on mobile */
    th:nth-child(3),
    td:nth-child(3),
    th:nth-child(4),
    td:nth-child(4) {
        display: none;
    }

    /* Adjust remaining column widths */
    th:nth-child(1),
    td:nth-child(1) {
        width: 40%;
    }
    
    th:nth-child(2),
    td:nth-child(2) {
        width: 45%;
    }
    
    th:last-child,
    td:last-child {
        width: 15%;
    }

    /* Development warning positioning */
    :deep(.development-warning) {
        margin-top: -1rem;
        margin-bottom: 1rem;
    }
}

.format-name {
    font-size: 0.9em;
    color: #666;
    margin-top: 4px;
}
</style>